// BUILD-042 checklist assignment panel.
//
// Cycle 13 (CHECKLIST workstream): this is the compact panel that opens BENEATH the "+ Assign checklist" action (see checklist/ChecklistWorkArea.tsx); it is never a permanent
// form at the bottom of a page. Cancel closes it without sending anything. An error keeps the panel open with every entered value.
//
// The date field is the per-assignment ANCHOR (hr_checklist_assignment.anchor_date): the server stores it with the assignment and computes every task's due date as
// anchor + the template task's day offset, once, at assignment time. It is therefore labelled truthfully ("Count due dates from (hire date | separation date)"), explained in
// one line, and followed by a calculated-date preview of the chosen template's tasks. It is NOT presented as the authoritative hire date, and changing a template or a hire
// date later never moves dates that were already assigned (see C:\Kiro\_work\reports\b042-corrections\c13-checklist-date-contract.md).
//
// Duplicate semantics follow the SERVER contract exactly (handleAssignChecklist): the same template is refused (409 with existingAssignmentId) while the earlier assignment still has
// an open task (not started / in progress) or no tasks at all; a fully finished one is history, so assigning it again deliberately creates a SEPARATE assignment; a different
// template is always allowed. There is no allow-duplicate switch on the server, so this panel offers no control that pretends to override a refusal: it shows the server's message
// and a link to the existing assignment. A synchronous in-flight guard means a fast double-click can never send two requests.
import { useId, useMemo, useRef, useState } from 'react';
import { assignHrChecklist, type HrChecklistAssignment, type HrChecklistTemplate } from '../../api/client';
import { formatLocalDate } from './hrFormat';
import { actionErrorMessage, needsPermissionMessage, PERMISSION_LABEL, useSurfaceFlag } from './workspace/loadState';
import { buildDuePreview, dateFieldExplanation, dateFieldLabel, findFinishedForTemplate, isRealDate } from './checklist/checklistModel';

type Status = { text: string; existingAssignmentId?: string } | null;

export function AssignChecklistForm({
  employeeId, templates, existingAssignments, onAssigned,
  kind, defaultAnchorDate, onCancel, onViewExisting,
}: {
  /** Optional: pre-fills the date (the hire date for onboarding; the scheduled last working day for offboarding when known). It stays editable. */
  defaultAnchorDate?: string;
  employeeId: string;
  templates: HrChecklistTemplate[];
  existingAssignments: HrChecklistAssignment[];
  /** Called after the server accepted the assignment (the created assignment is passed when the server returned it). */
  onAssigned: (created?: HrChecklistAssignment) => void;
  /** The process kind the caller already limited `templates` to; it decides the wording of the date field. */
  kind?: 'onboarding' | 'offboarding';
  onCancel?: () => void;
  /** When the server says "already assigned and still open", the way to open that exact existing assignment. */
  onViewExisting?: (assignmentId: string) => void;
}) {
  const ids = useId();
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [anchorDate, setAnchorDate] = useState(defaultAnchorDate ?? '');
  const [status, setStatus] = useState<Status>(null);
  const [assigning, setAssigning] = useState(false);
  // A synchronous guard: React state only updates after the click handler returns, so two clicks in the same tick would both see `assigning === false`.
  const inFlight = useRef(false);
  // When the server has reported that the caller lacks company-scope "Manage employee checklists", assigning is disabled with the reason. Unknown (older server) = not disabled.
  const cannotAssign = useSurfaceFlag('mayManageEmployeeChecklists') === false;
  const permissionMessage = needsPermissionMessage(PERMISSION_LABEL.employeeChecklists, 'assign checklists');

  const selected = templates.find((t) => t.templateId === selectedTemplateId) ?? null;
  const validDate = isRealDate(anchorDate);
  const preview = useMemo(() => (selected && validDate ? buildDuePreview(selected.tasks ?? [], anchorDate) : []), [selected, validDate, anchorDate]);
  const finishedEarlier = selected ? findFinishedForTemplate(existingAssignments, selected.templateId) : undefined;
  const canSubmit = !!selectedTemplateId && validDate && !assigning && !cannotAssign;

  async function submit() {
    if (inFlight.current || !selectedTemplateId || !validDate || cannotAssign) return;
    inFlight.current = true;
    setAssigning(true);
    setStatus(null);
    try {
      const created = await assignHrChecklist(employeeId, selectedTemplateId, anchorDate);
      onAssigned(created);
    } catch (e: any) {
      const existingAssignmentId = e?.status === 409 && typeof e?.body?.existingAssignmentId === 'string' ? e.body.existingAssignmentId : undefined;
      setStatus({ text: actionErrorMessage(e, PERMISSION_LABEL.employeeChecklists, 'assign checklists', 'Failed to assign checklist.'), existingAssignmentId });
    } finally {
      inFlight.current = false;
      setAssigning(false);
    }
  }

  const fieldCls = 'border border-gray-300 rounded px-3 py-1.5 text-sm max-w-full bg-white';
  const kindWord = kind === 'offboarding' ? 'Offboarding' : kind === 'onboarding' ? 'Onboarding' : '';
  const clearError = () => setStatus(null);

  return (
    <form
      className="rounded-lg border border-gray-200 bg-gray-50 p-4" data-testid="checklist-assign-panel" aria-label="Assign a checklist"
      onSubmit={(e) => { e.preventDefault(); void submit(); }}
    >
      <div className="flex gap-x-4 gap-y-3 flex-wrap items-start">
        <div className="flex flex-col gap-1 text-xs text-gray-600 min-w-0 flex-1 basis-56">
          <label htmlFor={`${ids}-tpl`}>{kindWord ? `${kindWord} checklist` : 'Checklist'}</label>
          <select
            id={`${ids}-tpl`} className={fieldCls} value={selectedTemplateId} autoFocus
            onChange={(e) => { setSelectedTemplateId(e.target.value); clearError(); }}
            data-testid="checklist-template-select"
          >
            <option value="">{kind ? `Select ${kind} template…` : 'Select a template…'}</option>
            {templates.map((t) => <option key={t.templateId} value={t.templateId}>{kind ? t.name : `${t.name} (${t.processKind})`}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1 text-xs text-gray-600 min-w-0">
          <label htmlFor={`${ids}-date`}>{dateFieldLabel(kind)}</label>
          <input
            id={`${ids}-date`} type="date" className={fieldCls} value={anchorDate} aria-describedby={`${ids}-date-help`}
            onChange={(e) => { setAnchorDate(e.target.value); clearError(); }}
            data-testid="checklist-start-date"
          />
        </div>
      </div>
      <p id={`${ids}-date-help`} className="text-[11px] text-gray-500 mt-1.5" data-testid="checklist-start-date-hint">{dateFieldExplanation(kind)}</p>

      {finishedEarlier && (
        <p className="mt-2 text-xs text-gray-600" data-testid="checklist-finished-earlier">
          An earlier copy of this checklist (assigned {formatLocalDate(finishedEarlier.assignedAt)}) is finished. Assigning it again creates a separate new checklist and keeps the earlier one as it is.
        </p>
      )}

      {selected && (
        <div className="mt-3" data-testid="checklist-date-preview">
          <div className="text-xs font-medium text-gray-700 mb-1">Calculated due dates</div>
          {!validDate ? (
            <p className="text-xs text-gray-500">Choose a date to see when each task would be due.</p>
          ) : preview.length === 0 ? (
            <p className="text-xs text-gray-500">This template has no tasks.</p>
          ) : (
            <ul className="text-xs text-gray-700 divide-y divide-gray-200 border border-gray-200 rounded-md bg-white max-h-52 overflow-auto">
              {preview.map((p, i) => (
                <li key={i} className="flex flex-wrap items-baseline justify-between gap-x-3 px-3 py-1.5" data-testid="checklist-preview-row">
                  <span className="min-w-0 break-words">{p.taskText}</span>
                  <span className="text-gray-600 whitespace-nowrap">{formatLocalDate(p.dueDate)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {cannotAssign && <div className="mt-2 text-xs text-amber-800" data-testid="checklist-assign-needs-permission">{permissionMessage}</div>}
      {status && (
        <div role="alert" className="mt-3 text-sm text-red-700" data-testid="checklist-assign-error">
          {status.text}
          {status.existingAssignmentId && onViewExisting && (
            <> <button type="button" className="underline text-[#176A9B]" onClick={() => onViewExisting(status.existingAssignmentId!)} data-testid="checklist-view-existing">View the existing checklist</button></>
          )}
        </div>
      )}

      <div className="flex gap-3 items-center mt-4">
        <button
          type="submit" disabled={!canSubmit} title={cannotAssign ? permissionMessage : undefined} data-testid="checklist-assign"
          className="bg-[#4AABDE] text-white text-sm font-medium px-4 py-1.5 rounded-md hover:bg-[#3b96c4] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {assigning ? 'Assigning…' : 'Assign'}
        </button>
        {onCancel && <button type="button" onClick={onCancel} disabled={assigning} className="text-sm text-gray-600 hover:underline px-1 disabled:opacity-50" data-testid="checklist-assign-cancel">Cancel</button>}
      </div>
    </form>
  );
}
